import React, { useState, useCallback } from 'react';
import { gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { Modal } from '../common/Modal';
import { TextInput } from '../common/TextInput';
import Button from '../common/Button';
import { FaPlus, FaTimes } from 'react-icons/fa';
import AdSearchSelect from '../common/SearchSelect/Ad_SearchSelect';
import { AdSearchSelect_AdOption } from '../common/SearchSelect/Ad_SearchSelect';
import AdVariation_SearchSelect, { AdVariationSearchSelect_AdVariationOption } from '../common/SearchSelect/AdVariation_SearchSelect';
import SupplierVariation_SearchSelect, { SupplierVariationSearchSelect_SupplierVariationOption } from '../common/SearchSelect/SupplierVariation_SearchSelect';
import { DeliveryDestination } from '../../../common/deliveryDestinations';
import RestockVariation_SearchSelect, { RestockVariationSearchSelect_RestockVariationOption } from '../common/SearchSelect/RestockVariation_SearchSelect';
import { useRequestManager } from '../common/RequestManager';
import client from '../../graphqlClient';
import { AddLineItemModal_AddLineItemMutation, AddLineItemModal_AddLineItemMutationVariables, AddLineItemModal_CreateDirectSaleLineItemMutation, AddLineItemModal_CreateDirectSaleLineItemMutationVariables } from '../../../codegen/graphql';
import { VATRatePerCountry } from '../../../common/VATRatePerCountry';
import Big from 'big.js';

/* Adds a line item to the order */
export default function AddLineItemModal({
  orderId,
  destinationCountry,
  isDirectSale,
  onClose,
}: {
  orderId: string;
  destinationCountry: DeliveryDestination;
  isDirectSale: boolean;
  onClose: () => void;
}) {
  const { trackRequest } = useRequestManager();

  const [creating, setCreating] = useState(false);
  const [selectedAd, setSelectedAd] = useState<AdSearchSelect_AdOption | null>(null);
  const [selectedAdVariation, setSelectedAdVariation] = useState<AdVariationSearchSelect_AdVariationOption | null>(null);
  const [selectedSupplierVariation, setSelectedSupplierVariation] = useState<SupplierVariationSearchSelect_SupplierVariationOption | null>(null);
  const [selectedRestockVariation, setSelectedRestockVariation] = useState<RestockVariationSearchSelect_RestockVariationOption | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [grossSale, setGrossSale] = useState(0);
  const [grossSaleVat, setGrossSaleVat] = useState(0);

  // Handle ad selection
  const handleAdSelect = useCallback((ad: AdSearchSelect_AdOption | null) => {
    setSelectedAd(ad);
    setSelectedAdVariation(null);
  }, []);

  // Handle variation selection
  const handleVariationSelect = useCallback((variation: AdVariationSearchSelect_AdVariationOption | null) => {
    setSelectedAdVariation(variation);
  }, []);

  // Handle supplier variation selection
  const handleSupplierVariationSelect = useCallback((supplierVariation: SupplierVariationSearchSelect_SupplierVariationOption | null) => {
    setSelectedSupplierVariation(supplierVariation);
  }, []);

  // Handle restock variation selection
  const handleRestockVariationSelect = useCallback((restockVariation: RestockVariationSearchSelect_RestockVariationOption | null) => {
    setSelectedRestockVariation(restockVariation);
  }, []);

  // Dynamically updates VAT depending on amount
  const handleGrossSaleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const countryVATRate = VATRatePerCountry[destinationCountry];
    const grossSale = new Big(e.target.value);
    setGrossSale(grossSale.toNumber());
    setGrossSaleVat(grossSale.times(countryVATRate.reverseExtractCoeff).round(2).toNumber());
  }, []);

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedAdVariation || !selectedSupplierVariation || !quantity) {
      toast.error('Please fill all fields');
      return;
    }

    setCreating(true);

    const lineItemPayload = {
      orderId,
      variationId: selectedAdVariation.id,
      quantity: parseInt(quantity.toString()),
      supplierProductId: selectedSupplierVariation.supplierProductId,
      supplierSku: selectedSupplierVariation.sku,
      restockShipmentProductVariationId: selectedRestockVariation?.id,
    }

    if (isDirectSale) {
      await trackRequest(
        client.mutate<
          AddLineItemModal_CreateDirectSaleLineItemMutation,
          AddLineItemModal_CreateDirectSaleLineItemMutationVariables
        >({
          mutation: CREATE_DIRECT_SALE_LINE_ITEM_MUTATION,
          variables: {
            destinationCountry: destinationCountry,
            grossSale: grossSale,
            grossSaleVat: grossSaleVat,
            channelFee: 0,
            channelFeeVat: 0,
            input: lineItemPayload,
          },
        }), {
        title: 'Adding line item',
        isBlocking: true,
      });
    } else {
      await trackRequest(
        client.mutate<AddLineItemModal_AddLineItemMutation, AddLineItemModal_AddLineItemMutationVariables>({
          mutation: ADD_LINE_ITEM_MUTATION,
          variables: {
            input: lineItemPayload,
          },
        }), {
          title: 'Adding line item',
          isBlocking: true,
        }
      );
    }
    setCreating(false);
  };

  return (
    <Modal
      width="large"
      onClose={onClose}
      title="Add New Line to Order"
    >
      <div className="space-y-6">

        {/* Ad Selection with Integrated Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search and Select Ad
          </label>
          <AdSearchSelect
            selectedAd={selectedAd}
            onAdChange={handleAdSelect}
          />
        </div>

        {/* Variation Selection */}
        {selectedAd && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Variation
            </label>
            <AdVariation_SearchSelect
              selectedAdVariation={selectedAdVariation}
              onAdVariationChange={handleVariationSelect}
              adId={selectedAd.id}
            />
          </div>
        )}

        {selectedAdVariation && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Supplier Product Variation
            </label>
            <SupplierVariation_SearchSelect
              selectedSupplierVariation={selectedSupplierVariation}
              onSupplierVariationChange={handleSupplierVariationSelect}
              linkedToAdVariationId={selectedAdVariation.id}
              deliveryDestination={destinationCountry}
            />
          </div>
        )}

        {selectedSupplierVariation
        && selectedSupplierVariation.supplierName === 'FR_ilyes'
        && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Supplier Product Variation
            </label>
            <RestockVariation_SearchSelect
              selectedRestockVariation={selectedRestockVariation}
              onRestockVariationChange={handleRestockVariationSelect}
              supplierProductId={selectedSupplierVariation.supplierProductId}
              supplierVariationSku={selectedSupplierVariation.sku}
            />
          </div>
        )}

        <TextInput
          type="number"
          min="1"
          label="Quantity"
          value={quantity}
          onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
          className="w-full"
        />

        {isDirectSale && (
          <div className="flex gap-2">
            <TextInput
              type="number"
              min="0"
              label="Gross Sale"
              value={grossSale}
              onChange={handleGrossSaleChange}
              className="w-full"
            />
            <TextInput
              type="number"
              min="0"
              label="Gross Sale Vat"
              value={grossSaleVat}
              onChange={(e) => setGrossSaleVat(Number(e.target.value))}
              className="w-full"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            onClick={onClose}
            preset="white"
            disabled={creating}
          >
            <FaTimes className="mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            preset="primary"
            disabled={
              !selectedAdVariation
              || !selectedSupplierVariation
              || (!selectedRestockVariation && selectedSupplierVariation.supplierName === 'FR_ilyes')
              || !quantity
            }
          >
            <FaPlus className="mr-2" />
            Add to Order
          </Button>
        </div>
      </div>
    </Modal>
  );
};


export const ADD_LINE_ITEM_MUTATION = gql`
  mutation AddLineItemModal_AddLineItem($input: CreateOrderProductsVariationInput!) {
    createOrderProductsVariation(input: $input) {
      id
      orderId
    }
  }
`;

export const CREATE_DIRECT_SALE_LINE_ITEM_MUTATION = gql`
  mutation AddLineItemModal_CreateDirectSaleLineItem(
    $destinationCountry: String!
    $grossSale: Float!
    $grossSaleVat: Float!
    $channelFee: Float!
    $channelFeeVat: Float!
    $input: CreateDirectSaleLineItemInput!
  ) {
    createDirectSaleLineItem(
      destinationCountry: $destinationCountry
      grossSale: $grossSale
      grossSaleVat: $grossSaleVat
      channelFee: $channelFee
      channelFeeVat: $channelFeeVat
      input: $input
    ) {
      id
      orderId
    }
  }
`;
